export type MetricAggregationType = "avg" | "max" | "min";

export interface PerformanceTier {
  id: string;
  label: string;
  shortLabel: string;
  color: string;
  hexColor: string;
  bgHexColor: string;
  minScore: number;
}

export const PERFORMANCE_TIERS: Record<string, PerformanceTier> = {
  EXCEPTIONAL: {
    id: "EXCEPTIONAL",
    label: "卓越表現 (90% 以上)",
    shortLabel: "卓越表現",
    color: "teal",
    hexColor: "#099268",
    bgHexColor: "#e6fcf5",
    minScore: 0.9,
  },
  SOLID: {
    id: "SOLID",
    label: "優良穩定 (75% - 89%)",
    shortLabel: "優良穩定",
    color: "blue",
    hexColor: "#1c7ed6",
    bgHexColor: "#e7f5ff",
    minScore: 0.75,
  },
  EXPECTED: {
    id: "EXPECTED",
    label: "符合預期 (60% - 74%)",
    shortLabel: "符合預期",
    color: "cyan",
    hexColor: "#1098ad",
    bgHexColor: "#e3fafc",
    minScore: 0.6,
  },
  EXPANSION_OPPORTUNITY: {
    id: "EXPANSION_OPPORTUNITY",
    label: "知識庫持續強化 (< 60%)",
    shortLabel: "持續強化",
    color: "gray",
    hexColor: "#868e96",
    bgHexColor: "#f1f3f5",
    minScore: 0.0,
  },
};

export function getPerformanceTier(score: number | null | undefined): PerformanceTier {
  if (score == null || Number.isNaN(score)) {
    return {
      id: "NO_DATA",
      label: "尚無數據",
      shortLabel: "無數據",
      color: "gray",
      hexColor: "#adb5bd",
      bgHexColor: "#f8f9fa",
      minScore: 0,
    };
  }
  if (score >= 0.9) return PERFORMANCE_TIERS.EXCEPTIONAL;
  if (score >= 0.75) return PERFORMANCE_TIERS.SOLID;
  if (score >= 0.6) return PERFORMANCE_TIERS.EXPECTED;
  return PERFORMANCE_TIERS.EXPANSION_OPPORTUNITY;
}

export interface GlossaryItem {
  id: string;
  nameZh: string;
  nameEn: string;
  definition: string;
  businessValue: string;
}

export const EVALUATION_GLOSSARY: GlossaryItem[] = [
  {
    id: "nv_accuracy",
    nameZh: "答案準確度",
    nameEn: "Answer Accuracy",
    definition: "衡量 AI 生成的回答與標準參考答案 (Reference) 的語意契合與事實一致性，評估模型是否回答正確。",
    businessValue: "確保客戶獲得正確、無誤導性的真實資訊，大幅降低人工客服轉接與澄清溝通成本。",
  },
  {
    id: "nv_context_relevance",
    nameZh: "內容相關性",
    nameEn: "Context Relevance",
    definition: "評估知識庫檢索模組 (Retriever) 所搜尋出的參考文檔片段，與使用者提問內容的相關程度。",
    businessValue: "檢驗企業私域知識庫之切分與檢索演算法是否能精準命中要點，避免檢索無關噪音干擾解答。",
  },
  {
    id: "nv_response_groundedness",
    nameZh: "回應根據性",
    nameEn: "Response Groundedness",
    definition: "檢驗 AI 的回覆內容是否完全有憑有據、嚴格本於檢索到的知識片段，杜絕模型自由編造。",
    businessValue: "企業級防幻覺 (Anti-Hallucination) 的核心屏障，確保 AI 回答絕不憑空捏造虛假承諾或不存在之資訊。",
  },
  {
    id: "rag_framework",
    nameZh: "RAG 檢索增強生成架構",
    nameEn: "Retrieval-Augmented Generation",
    definition: "將企業專有知識庫先經由語意向量搜尋比對，再將最相關的參考資料注入大語言模型進行彙整輸出。",
    businessValue: "兼具大語言模型的流暢對話體驗，以及企業內部專有私域知識的高精準度與資料安全性。",
  },
];

export interface RawExecutionRun {
  response?: string | null;
  retrievedContexts?: string[] | null;
  metrics?: Record<string, { score?: number | null } | null> | null;
  chatTokenUsage?: Array<{ model?: string | null; inputTokens?: number | null; outputTokens?: number | null }> | null;
  evaluationTokenUsage?: Array<{ model?: string | null; inputTokens?: number | null; outputTokens?: number | null }> | null;
  chatDurationMs?: number | null;
  evaluationDurationMs?: number | null;
}

export interface RawEvaluationTestResult {
  sample?: {
    userInput?: string | null;
    reference?: string | null;
  } | null;
  executionResults?: RawExecutionRun[] | null;
}

export interface ProcessedReportItem {
  index: number;
  userInput: string;
  reference: string;
  aiResponse: string;
  accuracy: number | null;
  contextRelevance: number | null;
  groundedness: number | null;
  averageScore: number | null;
  tier: PerformanceTier;
  inputTokens: number;
  outputTokens: number;
  chatInputTokens: number;
  chatOutputTokens: number;
  evaluationInputTokens: number;
  evaluationOutputTokens: number;
  contextCount: number;
  retrievedContexts: string[];
  chatDurationMs: number | null;
  evaluationDurationMs: number | null;
}

export interface MetricSummary {
  key: string;
  nameZh: string;
  averageScore: number | null;
  validCount: number;
  tier: PerformanceTier;
}

export interface TierDistribution {
  tier: PerformanceTier;
  count: number;
  percentage: number;
}

export interface TokenUsageSummary {
  chatInputTokens: number;
  chatOutputTokens: number;
  chatTotalTokens: number;
  evaluationInputTokens: number;
  evaluationOutputTokens: number;
  evaluationTotalTokens: number;
  totalTokens: number;
  models: string[];
  chatModels?: string[];
  evaluationModels?: string[];
}

export interface DurationSummary {
  chatDurationAvg: number | null;
  chatDurationMin: number | null;
  chatDurationMax: number | null;
  evaluationDurationAvg: number | null;
  validChatCount: number;
  validEvalCount: number;
}

export interface EvaluationReportSummary {
  totalCount: number;
  overallAverageScore: number | null;
  overallTier: PerformanceTier;
  metrics: {
    accuracy: MetricSummary;
    contextRelevance: MetricSummary;
    groundedness: MetricSummary;
  };
  tierDistributions: TierDistribution[];
  tokenUsage: TokenUsageSummary;
  durationSummary: DurationSummary;
  items: ProcessedReportItem[];
}

function aggregateMetricList(
  executionResults: RawExecutionRun[] | undefined | null,
  key: string,
  mode: MetricAggregationType
): number | null {
  if (!executionResults || executionResults.length === 0) return null;

  const validScores: number[] = [];
  executionResults.forEach((run) => {
    const score = run.metrics?.[key]?.score;
    if (score != null && !Number.isNaN(score)) {
      validScores.push(score);
    }
  });

  if (validScores.length === 0) return null;

  if (mode === "max") return Math.max(...validScores);
  if (mode === "min") return Math.min(...validScores);

  const sum = validScores.reduce((a, b) => a + b, 0);
  return sum / validScores.length;
}

export function formatDurationSec(ms?: number | null): string {
  if (ms == null || Number.isNaN(ms)) return "-";
  return `${(ms / 1000).toFixed(2)} s`;
}

export function aggregateDurations(
  executionResults: RawExecutionRun[] | undefined | null,
  mode: MetricAggregationType | number | "default" = "avg"
): { chatDurationMs: number | null; evaluationDurationMs: number | null } {
  if (!executionResults || executionResults.length === 0) {
    return { chatDurationMs: null, evaluationDurationMs: null };
  }

  if (typeof mode === "number") {
    const selected = executionResults[mode];
    return {
      chatDurationMs: selected?.chatDurationMs ?? null,
      evaluationDurationMs: selected?.evaluationDurationMs ?? null,
    };
  }

  if (mode === "avg" || mode === "default") {
    const validChat = executionResults
      .map((r) => r.chatDurationMs)
      .filter((m): m is number => m != null && !Number.isNaN(m));
    const validEval = executionResults
      .map((r) => r.evaluationDurationMs)
      .filter((m): m is number => m != null && !Number.isNaN(m));

    return {
      chatDurationMs: validChat.length > 0 ? validChat.reduce((a, b) => a + b, 0) / validChat.length : null,
      evaluationDurationMs: validEval.length > 0 ? validEval.reduce((a, b) => a + b, 0) / validEval.length : null,
    };
  }

  let targetIndex = 0;
  let targetScore: number | null = null;
  for (let i = 0; i < executionResults.length; i++) {
    const run = executionResults[i];
    const scores = Object.values(run.metrics || {})
      .map((m) => m?.score)
      .filter((s): s is number => s != null && !Number.isNaN(s));
    if (scores.length > 0) {
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      if (targetScore == null) {
        targetScore = avg;
        targetIndex = i;
      } else if (mode === "max" && avg > targetScore) {
        targetScore = avg;
        targetIndex = i;
      } else if (mode === "min" && avg < targetScore) {
        targetScore = avg;
        targetIndex = i;
      }
    }
  }

  const selected = executionResults[targetIndex];
  return {
    chatDurationMs: selected?.chatDurationMs ?? null,
    evaluationDurationMs: selected?.evaluationDurationMs ?? null,
  };
}

export function buildEvaluationReportSummary(
  results: RawEvaluationTestResult[] = [],
  mode: MetricAggregationType = "avg"
): EvaluationReportSummary {
  let chatInputTokens = 0;
  let chatOutputTokens = 0;
  let evalInputTokens = 0;
  let evalOutputTokens = 0;
  const chatModelSet = new Set<string>();
  const evalModelSet = new Set<string>();

  const items: ProcessedReportItem[] = results.map((result, idx) => {
    const executions = result.executionResults ?? [];
    const firstRun = executions[0];

    // Pick representative response
    const aiResponse = (executions.length > 0 && executions[0]?.response) || "無回應";
    const retrievedContexts = (executions.length > 0 && executions[0]?.retrievedContexts) || [];

    const accuracy = aggregateMetricList(executions, "nv_accuracy", mode);
    const contextRelevance = aggregateMetricList(executions, "nv_context_relevance", mode);
    const groundedness = aggregateMetricList(executions, "nv_response_groundedness", mode);
    const { chatDurationMs, evaluationDurationMs } = aggregateDurations(executions, mode);

    const validMetrics = [accuracy, contextRelevance, groundedness].filter(
      (s): s is number => s != null && !Number.isNaN(s)
    );
    const averageScore =
      validMetrics.length > 0
        ? validMetrics.reduce((a, b) => a + b, 0) / validMetrics.length
        : null;

    let itemChatInTokens = 0;
    let itemChatOutTokens = 0;
    let itemEvalInTokens = 0;
    let itemEvalOutTokens = 0;

    executions.forEach((run) => {
      run.chatTokenUsage?.forEach((t) => {
        chatInputTokens += t.inputTokens ?? 0;
        chatOutputTokens += t.outputTokens ?? 0;
        itemChatInTokens += t.inputTokens ?? 0;
        itemChatOutTokens += t.outputTokens ?? 0;
        if (t.model) chatModelSet.add(t.model);
      });
      run.evaluationTokenUsage?.forEach((t) => {
        evalInputTokens += t.inputTokens ?? 0;
        evalOutputTokens += t.outputTokens ?? 0;
        itemEvalInTokens += t.inputTokens ?? 0;
        itemEvalOutTokens += t.outputTokens ?? 0;
        if (t.model) evalModelSet.add(t.model);
      });
    });

    return {
      index: idx + 1,
      userInput: result.sample?.userInput || "",
      reference: result.sample?.reference || "",
      aiResponse,
      accuracy,
      contextRelevance,
      groundedness,
      averageScore,
      tier: getPerformanceTier(averageScore),
      inputTokens: itemChatInTokens,
      outputTokens: itemChatOutTokens,
      chatInputTokens: itemChatInTokens,
      chatOutputTokens: itemChatOutTokens,
      evaluationInputTokens: itemEvalInTokens,
      evaluationOutputTokens: itemEvalOutTokens,
      contextCount: retrievedContexts.length,
      retrievedContexts,
      chatDurationMs,
      evaluationDurationMs,
    };
  });

  const calcMetricAverage = (key: "accuracy" | "contextRelevance" | "groundedness") => {
    const scores = items
      .map((i) => i[key])
      .filter((s): s is number => s != null && !Number.isNaN(s));
    if (scores.length === 0) return { avg: null, count: 0 };
    return {
      avg: scores.reduce((a, b) => a + b, 0) / scores.length,
      count: scores.length,
    };
  };

  const accSummary = calcMetricAverage("accuracy");
  const ctxSummary = calcMetricAverage("contextRelevance");
  const grdSummary = calcMetricAverage("groundedness");

  const validOverallScores = items
    .map((i) => i.averageScore)
    .filter((s): s is number => s != null && !Number.isNaN(s));
  const overallAverageScore =
    validOverallScores.length > 0
      ? validOverallScores.reduce((a, b) => a + b, 0) / validOverallScores.length
      : null;

  // Calculate tier distributions
  const tierCounts: Record<string, number> = {
    EXCEPTIONAL: 0,
    SOLID: 0,
    EXPECTED: 0,
    EXPANSION_OPPORTUNITY: 0,
  };

  items.forEach((item) => {
    if (item.tier.id in tierCounts) {
      tierCounts[item.tier.id]++;
    }
  });

  const scoredItemsCount = items.filter((i) => i.tier.id in tierCounts).length;
  const totalForPercentage = scoredItemsCount > 0 ? scoredItemsCount : (items.length || 1);
  const tierDistributions: TierDistribution[] = [
    {
      tier: PERFORMANCE_TIERS.EXCEPTIONAL,
      count: tierCounts.EXCEPTIONAL,
      percentage: Math.round((tierCounts.EXCEPTIONAL / totalForPercentage) * 100),
    },
    {
      tier: PERFORMANCE_TIERS.SOLID,
      count: tierCounts.SOLID,
      percentage: Math.round((tierCounts.SOLID / totalForPercentage) * 100),
    },
    {
      tier: PERFORMANCE_TIERS.EXPECTED,
      count: tierCounts.EXPECTED,
      percentage: Math.round((tierCounts.EXPECTED / totalForPercentage) * 100),
    },
    {
      tier: PERFORMANCE_TIERS.EXPANSION_OPPORTUNITY,
      count: tierCounts.EXPANSION_OPPORTUNITY,
      percentage: Math.round((tierCounts.EXPANSION_OPPORTUNITY / totalForPercentage) * 100),
    },
  ];

  const allChatDurations = items
    .map((i) => i.chatDurationMs)
    .filter((d): d is number => d != null && !Number.isNaN(d));
  const allEvalDurations = items
    .map((i) => i.evaluationDurationMs)
    .filter((d): d is number => d != null && !Number.isNaN(d));

  const allRunChatDurations: number[] = [];
  results.forEach((r) => {
    r.executionResults?.forEach((run) => {
      if (run.chatDurationMs != null && !Number.isNaN(run.chatDurationMs)) {
        allRunChatDurations.push(run.chatDurationMs);
      }
    });
  });

  const durationSummary: DurationSummary = {
    chatDurationAvg:
      allChatDurations.length > 0
        ? allChatDurations.reduce((a, b) => a + b, 0) / allChatDurations.length
        : null,
    chatDurationMin: allRunChatDurations.length > 0 ? Math.min(...allRunChatDurations) : null,
    chatDurationMax: allRunChatDurations.length > 0 ? Math.max(...allRunChatDurations) : null,
    evaluationDurationAvg:
      allEvalDurations.length > 0
        ? allEvalDurations.reduce((a, b) => a + b, 0) / allEvalDurations.length
        : null,
    validChatCount: allChatDurations.length,
    validEvalCount: allEvalDurations.length,
  };

  return {
    totalCount: items.length,
    overallAverageScore,
    overallTier: getPerformanceTier(overallAverageScore),
    metrics: {
      accuracy: {
        key: "nv_accuracy",
        nameZh: "答案準確度",
        averageScore: accSummary.avg,
        validCount: accSummary.count,
        tier: getPerformanceTier(accSummary.avg),
      },
      contextRelevance: {
        key: "nv_context_relevance",
        nameZh: "內容相關性",
        averageScore: ctxSummary.avg,
        validCount: ctxSummary.count,
        tier: getPerformanceTier(ctxSummary.avg),
      },
      groundedness: {
        key: "nv_response_groundedness",
        nameZh: "回應根據性",
        averageScore: grdSummary.avg,
        validCount: grdSummary.count,
        tier: getPerformanceTier(grdSummary.avg),
      },
    },
    tierDistributions,
    tokenUsage: {
      chatInputTokens,
      chatOutputTokens,
      chatTotalTokens: chatInputTokens + chatOutputTokens,
      evaluationInputTokens: evalInputTokens,
      evaluationOutputTokens: evalOutputTokens,
      evaluationTotalTokens: evalInputTokens + evalOutputTokens,
      totalTokens: chatInputTokens + chatOutputTokens + evalInputTokens + evalOutputTokens,
      models: Array.from(chatModelSet),
      chatModels: Array.from(chatModelSet),
      evaluationModels: Array.from(evalModelSet),
    },
    durationSummary,
    items,
  };
}

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value == null) return "";
  let str = String(value);
  if (/^[=+\-@]/.test(str)) {
    str = `'${str}`;
  }
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function generateEvaluationCsv(
  results: RawEvaluationTestResult[] = [],
  mode: MetricAggregationType = "avg"
): string {
  const summary = buildEvaluationReportSummary(results, mode);

  const headers = [
    "使用者提問",
    "標準參考回答",
    "AI實際回答",
    "答案準確度",
    "內容相關性",
    "回應根據性",
    "綜合平均分",
    "對話耗時(秒)",
    "評估耗時(秒)",
    "對話輸入Token",
    "對話輸出Token",
    "檢索片段數",
  ];

  const formatScore = (val: number | null) => (val != null ? (val * 100).toFixed(1) + "%" : "無評分");
  const formatDurationSecForCsv = (ms: number | null) =>
    ms != null && !Number.isNaN(ms) ? (ms / 1000).toFixed(2) : "";

  const rows = summary.items.map((item) => [
    item.userInput,
    item.reference,
    item.aiResponse,
    formatScore(item.accuracy),
    formatScore(item.contextRelevance),
    formatScore(item.groundedness),
    formatScore(item.averageScore),
    formatDurationSecForCsv(item.chatDurationMs),
    formatDurationSecForCsv(item.evaluationDurationMs),
    item.inputTokens,
    item.outputTokens,
    item.contextCount,
  ]);

  const csvBody = [headers, ...rows]
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\r\n");

  // Prepend UTF-8 BOM for Microsoft Excel compatibility
  return `\uFEFF${csvBody}`;
}
